import { useRef } from 'react';
import PropTypes from 'prop-types';
import BsModal from 'react-bootstrap/Modal';
import { className } from '../../../utils/GlobalCssProxy';
import noop from 'lodash/noop';
import InteractiveElement from '../interactive-element';

const modalBasicClass = className.string('modal--basic');
const modalSubtitleClass = className.string('modal-subtitle');


/**
 * Wrapper around the [react-boostrap Modal component](https://react-bootstrap.github.io/components/modal/).
 * This component moves the modal container alongside the modal component itself,
 * as opposed to the default container appended to the body, so that the modal is reliably
 * rendered beneath the MD F2 app root.
 *
 * Component has the same API and supports the functionality of the react-bootstrap component,
 * except for the `closeButton` prop of the `Header` sub-component.
 * Instead, used the `CloseButton` sub-component to render a close button.
 *
 */
const ModalBasic = ({ className: classNameProp, modalSm, modalNoColor, ...bsModalProps }) => {
  const modalContainer = useRef(null);

  const classes = classNameProp
    ? `${modalBasicClass} ${classNameProp}`
    : `${modalBasicClass}`;

  return (
    <>
      <BsModal
        container={modalContainer}
        {...bsModalProps}
        className={`${classes} ${modalSm ? className.string('modal-small') : ''} ${modalNoColor ? className.string('modal-no-color') : ''}`}
        centered={true}
      />
      <div ref={modalContainer}></div>
    </>
  );
};

ModalBasic.propTypes = {
  className: PropTypes.string,
  modalNoColor: PropTypes.bool,
  modalSm: PropTypes.bool
};

// Override Header subcomponent -- remove support for closeButton
const Header = ({ closeButton, ...bsHeaderProps }) => (
  <BsModal.Header {...bsHeaderProps} />
);

Header.propTypes = {
  closeButton: PropTypes.any
};

ModalBasic.Header = Header;

// Add CloseButton sub-component
// TODO: consider restoring default behavior and adding necessary styling in global.module.scss, a la .fade and .show.
const CloseButton = ({ onClick }) => {
  return (
    <InteractiveElement
      as="button"
      aria-label="Close"
      onClick={onClick}
      {...className('close', 'modal__close')}
    >
      <span aria-hidden="true">&times;</span>
    </InteractiveElement>
  );
};

CloseButton.propTypes = {
  onClick: PropTypes.func
};

CloseButton.defaultProps = {
  onClick: noop
};

ModalBasic.CloseButton = CloseButton;

const Title = ({ as, ...props }) => (
  <BsModal.Title as={as || 'div'} {...props} />
);

Title.propTypes = {
  as: PropTypes.string
};

ModalBasic.Title = Title;

// Add Subtitle sub-component
const Subtitle = ({ children }) => (
  <div className={modalSubtitleClass}>{children}</div>
);

Subtitle.propTypes = {
  children: PropTypes.node
};

ModalBasic.Subtitle = Subtitle;

// Add bootstrap-react sub-components
['Body', 'Footer'].forEach(subComponent => {
  ModalBasic[subComponent] = BsModal[subComponent];
});

export default ModalBasic;
